import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { switchMap, take, tap } from 'rxjs/operators';
import { trackImpersonationRequest } from './+state/impersonation.actions';
import { selectIsImpersonating } from './+state/impersonation.selectors';
import { SKIP_IMPERSONATION_LOG_METADATA } from './impersonation.constants';
import { IMPERSONATION_REQUEST_METADATA } from './models/impersonation-action-log.model';
import { ImpersonationService } from './impersonation.service';

export const impersonationInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_IMPERSONATION_LOG_METADATA)) {
    return next(req);
  }

  const store = inject(Store);
  const impersonationService = inject(ImpersonationService);

  return store.select(selectIsImpersonating).pipe(
    take(1),
    switchMap(isImpersonating => {
      if (!isImpersonating) {
        return next(req);
      }

      // Only check auth endpoints when impersonating (prevents CORS during silent renew)
      return impersonationService.isAuthEndpoint(req.url).pipe(
        take(1),
        switchMap(isAuthEndpoint => {
          if (isAuthEndpoint) {
            return next(req);
          }

          const requestWithMetadata = impersonationService.attachRequestMetadata(req);
          const isEventHubRequest = impersonationService.isEventHubRequest(req.url);
          const processedRequest$ = isEventHubRequest
            ? impersonationService.processEventHubRequest(requestWithMetadata, next)
            : impersonationService.processStandardRequest(requestWithMetadata, next);

          return processedRequest$.pipe(
            tap(event => {
              if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
                const metadata = requestWithMetadata.context.get(IMPERSONATION_REQUEST_METADATA);
                if (metadata) {
                  store.dispatch(trackImpersonationRequest({ ...metadata, statusCode: event.status }));
                }
              }
            })
          );
        })
      );
    })
  );
};
